/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { Component, useCallback, useState } from 'react';

import noop from 'lodash/noop';

import { cssMap, jsx } from '@atlaskit/css';
import Image from '@atlaskit/image';
import { Spotlight as AkSpotlight } from '@atlaskit/onboarding';
import { type Actions } from '@atlaskit/onboarding/types';
import { fg } from '@atlaskit/platform-feature-flags';
import { Text } from '@atlaskit/primitives/compiled';
import {
	PopoverContent,
	type PopoverContentProps,
	SpotlightActions,
	SpotlightBody,
	SpotlightCard,
	SpotlightControls,
	SpotlightDismissControl,
	SpotlightFooter,
	SpotlightHeader,
	SpotlightHeadline,
	SpotlightMedia,
	SpotlightPrimaryAction,
	SpotlightPrimaryLink,
	SpotlightSecondaryAction,
	SpotlightSecondaryLink,
} from '@atlaskit/spotlight';

import { type FireClickEvent } from '../../common/types/analytics';
import { type ButtonAction, buttonActionTypes } from '../../common/types/button';
import { translations } from '../../common/utils/translations';
import HtmlContent from '../../controllers/utils/html-content';

const styles = cssMap({
	image: {
		width: '100%',
	},
});

type ClickHandler = () => void;

type dialogPlacement =
	| 'top left'
	| 'top center'
	| 'top right'
	| 'right top'
	| 'right middle'
	| 'right bottom'
	| 'bottom left'
	| 'bottom center'
	| 'bottom right'
	| 'left top'
	| 'left middle'
	| 'left bottom';

type Props = {
	body?: string;
	fireClickEvent?: FireClickEvent;
	dialogPlacement?: dialogPlacement;
	dialogWidth?: number;
	title?: string;
	image?: string;
	primaryAction?: ButtonAction;
	primaryText?: string;
	pulse?: boolean;
	secondaryAction?: ButtonAction;
	secondaryText?: string;
	target?: string;
	targetBgColor?: string;
	targetRadius?: number;
	engagementId?: string;
	isShown?: boolean;
	offset?: PopoverContentProps['offset'];
	isMigrated?: boolean;
};

/**
 * EngagementSpotlight delegates to either the migrated @atlaskit/spotlight
 * implementation or the deprecated @atlaskit/onboarding Spotlight.
 * Control flow matches JiraSpotlight (early returns, single branch per render).
 */
export const EngagementSpotlight = ({ isMigrated = false, ...rest }: Props) => {
	if (isMigrated && fg('ads_spotlight_migration_engagekit_ts')) {
		return <MigratedAkSpotlight {...rest} />;
	}

	return <DeprecatedAkSpotlight {...rest} />;
};

// eslint-disable-next-line @repo/internal/react/no-class-components
class DeprecatedAkSpotlight extends Component<Props> {
	static defaultProps: {
		fireClickEvent: (...args: any[]) => void;
	} = {
			fireClickEvent: noop,
		};

	getButtonClickHandler(buttonType: string, action: ButtonAction): ClickHandler {
		const { fireClickEvent } = this.props;

		switch (action.type) {
			case buttonActionTypes.LINK:
				return () => {
					fireClickEvent!(`${buttonType}`);

					window.open(action.data!.url, action.data!.target);
				};
			default:
				return () => {
					fireClickEvent!(`${buttonType}`);
				};
		}
	}

	getButtonActions(): Actions {
		const { primaryAction, primaryText, secondaryAction, secondaryText } = this.props;
		const actions: Actions = [];

		if (secondaryAction && secondaryText) {
			actions.push({
				onClick: this.getButtonClickHandler('secondary', secondaryAction),
				text: secondaryText,
				appearance: secondaryAction.appearance,
			});
		}

		if (primaryAction && primaryText) {
			actions.push({
				onClick: this.getButtonClickHandler('primary', primaryAction),
				text: primaryText,
				appearance: primaryAction.appearance,
				autoFocus: true,
			});
		}

		return actions;
	}

	render(): React.JSX.Element {
		const {
			body,
			dialogPlacement,
			dialogWidth,
			title,
			image,
			pulse,
			target,
			targetBgColor,
			targetRadius,
		} = this.props;

		return (
			<AkSpotlight
				actions={this.getButtonActions()}
				dialogPlacement={dialogPlacement}
				dialogWidth={dialogWidth}
				heading={title}
				image={image}
				pulse={pulse}
				target={target}
				targetBgColor={targetBgColor}
				targetRadius={targetRadius}
			>
				<HtmlContent html={body!} />
			</AkSpotlight>
		);
	}
}

const MigratedAkSpotlight = (props: Props) => {
	const placement = translations.dialogPlacement(props.dialogPlacement);
	const [isVisible, setIsVisible] = useState(true);
	const dismiss = useCallback(() => {
		setIsVisible(false);
	}, [setIsVisible]);

	return (
		<PopoverContent
			offset={props.offset}
			dismiss={dismiss}
			placement={placement}
			isVisible={isVisible}
		>
			<SpotlightCard>
				<SpotlightHeader>
					<SpotlightHeadline>{props.title}</SpotlightHeadline>
					<SpotlightControls>
						<SpotlightDismissControl />
					</SpotlightControls>
				</SpotlightHeader>
				{props.image && (
					<SpotlightMedia>
						<Image css={styles.image} src={props.image} alt="" />
					</SpotlightMedia>
				)}
				<SpotlightBody>
					<Text>{props.body}</Text>
				</SpotlightBody>
				<SpotlightFooter>
					<SpotlightActions>
						{props.secondaryAction && (
							(props.secondaryAction?.type === 'link' ? (
								<SpotlightSecondaryLink
									onClick={dismiss}
									{...props.secondaryAction.data}
									href={props.secondaryAction.data?.url || ''}
									target={props.secondaryAction.data?.target}
								>
									{props.secondaryText}
								</SpotlightSecondaryLink>
							) : (
								<SpotlightSecondaryAction onClick={dismiss} {...props.secondaryAction.data}>
									{props.secondaryText}
								</SpotlightSecondaryAction>
							))
						)}
						{props.primaryAction && (
							(props.primaryAction?.type === 'link' ? (
								<SpotlightPrimaryLink
									onClick={dismiss}
									{...props.primaryAction.data}
									href={props.primaryAction.data?.url || ''}
									target={props.primaryAction.data?.target}
								>
									{props.primaryText}
								</SpotlightPrimaryLink>
							) : (
								<SpotlightPrimaryAction onClick={dismiss} {...props.primaryAction.data}>
									{props.primaryText}
								</SpotlightPrimaryAction>
							))
						)}
					</SpotlightActions>
				</SpotlightFooter>
			</SpotlightCard>
		</PopoverContent>
	);
};

export default EngagementSpotlight;
